import React, { useEffect, useRef } from 'react';
import css from './CheckoutPage.module.css';
import { types as sdkTypes } from '../../util/sdkLoader';
import { FieldRadioButton } from '../../components';
import { FormattedMessage, useIntl } from 'react-intl';
import { formatMoney, convertMoneyToNumber } from '../../util/currency';
import { formatListingForAnalytics, trackEvent } from '../../util/analytics';
import { IoShieldCheckmark } from 'react-icons/io5';
import { FaHandshake } from 'react-icons/fa6';
const FREE_AUTHENTICATED_SHIPPING = 50000;
const { Money } = sdkTypes;
const POP_NOUVELLE_USER = process.env.REACT_APP_POP_NOUVELLE_USER;
const shippingCost = process.env.REACT_APP_SHIPPING_FEE || 2;
const advanceShippingCost = process.env.REACT_APP_ADVANCE_SHIPPING_COST || 15;
const PurchaseType = props => {
  const {
    values,
    pageData,
    fetchSpeculatedTransaction,
    promoCode,
    form,
    handleShowShippingDetails,
    handleShowAuthenticatedShipping,
  } = props;
  const { listing } = pageData || {};
  const { author, attributes } = listing || {};
  const { price, publicData } = attributes || {};
  const isCreatedByScript = publicData?.created_by == 'script';
  const isAmountGreaterOrEqualTo500 = price.amount >= FREE_AUTHENTICATED_SHIPPING;
  const { id } = author || {};
  const authorId = id?.uuid;

  const intl = useIntl();
  const advanceShipping = +advanceShippingCost * 100;

  // Determine shipping fee based on selected country
  const recipientCountry = values?.recipientCountry;
  const hasCountrySelected = !!recipientCountry;
  let shippingFeeAmount;
  if (recipientCountry === 'CY') {
    shippingFeeAmount = 1500; // €15.00 for Cyprus
  } else if (recipientCountry && recipientCountry !== 'GR') {
    shippingFeeAmount = 2000; // €20.00 for other EU countries
  } else if (recipientCountry === 'GR') {
    shippingFeeAmount = +shippingCost * 100; // Default (Greece) from env
  } else {
    shippingFeeAmount = 0; // No country selected yet
  }

  const advanceShippingCostMoney = new Money(
    advanceShipping,
    process.env.REACT_APP_SHARETRIBE_MARKETPLACE_CURRENCY || 'EUR'
  );
  const shippingCostMoney = new Money(
    shippingFeeAmount,
    process.env.REACT_APP_SHARETRIBE_MARKETPLACE_CURRENCY || 'EUR'
  );
  const advanceShippingCostFormatted = formatMoney(intl, advanceShippingCostMoney);
  const shippingCostFormatted = hasCountrySelected ? formatMoney(intl, shippingCostMoney, true) : '–';
  const isPopNouvelleUser = authorId === POP_NOUVELLE_USER || isCreatedByScript;

  // Track add_shipping_info once per distinct shipping method selection.
  const lastTrackedShippingTypeRef = useRef(null);

  useEffect(() => {
    if (isPopNouvelleUser) {
      form.change('shippingType', 'direct');
    } else if (isAmountGreaterOrEqualTo500) {
      form.change('shippingType', 'authentication');
    }
  }, []);
  useEffect(() => {
    if (values.shippingType) {
      const { quantity, deliveryMethod } = pageData?.orderData || {};
      const stockReservationQuantity = Number.parseInt(quantity, 10);
      const deliveryMethodMaybe = deliveryMethod ? { deliveryMethod } : {};
      const recipientCountryMaybe = values.recipientCountry
        ? { recipientCountry: values.recipientCountry }
        : {};
      const params = {
        listingId: pageData?.listing?.id,
        shippingType: values.shippingType,
        ...recipientCountryMaybe,
        ...deliveryMethodMaybe,
        quantity: stockReservationQuantity,
      };
      const processAlias = 'advance-default-purchase/release-1';
      const txId = pageData?.transaction?.id;
      const transitionName = 'transition/request-payment';
      const isPrivileged = true;
      fetchSpeculatedTransaction(params, processAlias, txId, transitionName, isPrivileged, promoCode);

      // Push add_shipping_info when the shipping method actually changes.
      if (values.shippingType !== lastTrackedShippingTypeRef.current) {
        lastTrackedShippingTypeRef.current = values.shippingType;
        const item = listing ? formatListingForAnalytics(listing, {}) : null;
        if (item && stockReservationQuantity) {
          item.quantity = stockReservationQuantity;
        }
        trackEvent('add_shipping_info', {
          currency: price?.currency || process.env.REACT_APP_SHARETRIBE_MARKETPLACE_CURRENCY || 'EUR',
          value: item ? convertMoneyToNumber(price) * (item.quantity || 1) : 0,
          shipping_tier:
            isPopNouvelleUser || values.shippingType === 'authentication'
              ? 'Authenticated Shipping'
              : 'Direct Shipping',
          items: item ? [item] : [],
        });
      }
    }
  }, [values.shippingType, values.recipientCountry]);

  return (
    <div className={css.shippingContainer}>
      <h3 className={css.shippingHeading}>
        <FormattedMessage id="CheckoutPage.PurchaseType.shippingTypeLabel" />
      </h3>
      {isPopNouvelleUser ? (
        <FieldRadioButton
          id={'direct'}
          name="shippingType"
          className={css.shippingType}
          svgClassName={css.notShow}
          label={
            <div className={css.container}>
              <p className={css.headingText}>
                <IoShieldCheckmark className={css.icon} />
                <FormattedMessage id="CheckoutPage.PurchaseType.authentication" />
              </p>
              <p className={css.subHeading}>
                <FormattedMessage
                  id="CheckoutPage.PurchaseType.authenticationSubheading"
                  values={{ advanceShippingCostFormatted: advanceShippingCostFormatted }}
                />
                <p className={css.priceLabel}>
                  <FormattedMessage id="CheckoutPage.PurchaseType.includedLabel" />
                </p>
              </p>
              {hasCountrySelected ? (
                <p className={css.subHeading}>
                  <FormattedMessage
                    id="CheckoutPage.PurchaseType.shippingOnly"
                    values={{ price: shippingCostFormatted }}
                  />
                  {recipientCountry === 'GR' ? (
                    <p className={css.priceLabel}>
                      <FormattedMessage id="CheckoutPage.PurchaseType.free" />
                    </p>
                  ) : (
                    <p className={css.priceLabel}>{shippingCostFormatted}</p>
                  )}
                </p>
              ) : null}
              <p className={css.learnMore} onClick={() => handleShowAuthenticatedShipping(true)}>
                <FormattedMessage id="CheckoutPage.PurchaseType.learnMore" />
              </p>
            </div>
          }
          value="direct"
        />
      ) : isAmountGreaterOrEqualTo500 ? (
        <FieldRadioButton
          id={'authentication'}
          name="shippingType"
          className={css.shippingType}
          svgClassName={css.notShow}
          label={
            <div className={css.container}>
              <p className={css.headingText}>
                <IoShieldCheckmark className={css.icon} />
                <FormattedMessage id="CheckoutPage.PurchaseType.authentication" />
              </p>
              <p className={css.subHeading}>
                <FormattedMessage
                  id="CheckoutPage.PurchaseType.authenticationSubheading"
                  values={{ advanceShippingCostFormatted: advanceShippingCostFormatted }}
                />
                <p className={css.priceLabel}>
                  <FormattedMessage id="CheckoutPage.PurchaseType.includedLabel" />
                </p>
              </p>
              {hasCountrySelected ? (
                <p className={css.subHeading}>
                  <FormattedMessage
                    id="CheckoutPage.PurchaseType.shippingOnly"
                    values={{ price: shippingCostFormatted }}
                  />
                  <p className={css.priceLabel}>{shippingCostFormatted}</p>
                </p>
              ) : null}
              <p className={css.learnMore} onClick={() => handleShowAuthenticatedShipping(true)}>
                <FormattedMessage id="CheckoutPage.PurchaseType.learnMore" />
              </p>
            </div>
          }
          value="authentication"
        />
      ) : (
        <div className={css.typeContainer}>
          <FieldRadioButton
            id={'direct'}
            name="shippingType"
            className={css.shippingType}
            label={
              <div className={css.container}>
                <p className={css.headingText}>
                  <FaHandshake className={css.icon} />
                  <FormattedMessage id="CheckoutPage.PurchaseType.directShippingLabel" />
                </p>
                {hasCountrySelected ? (
                  <p className={css.subHeading}>
                    <FormattedMessage
                      id="CheckoutPage.PurchaseType.shippingOnly"
                      values={{ price: shippingCostFormatted }}
                    />
                    <p className={css.priceLabel}>{shippingCostFormatted}</p>
                  </p>
                ) : null}
                <p className={css.learnMore} onClick={() => handleShowShippingDetails(true)}>
                  <FormattedMessage id="CheckoutPage.PurchaseType.learnMore" />
                </p>
              </div>
            }
            value="direct"
          />
          <FieldRadioButton
            id={'authentication'}
            name="shippingType"
            className={css.shippingType}
            label={
              <div className={css.container}>
                <p className={css.headingText}>
                  <IoShieldCheckmark className={css.icon} />
                  <FormattedMessage id="CheckoutPage.PurchaseType.authentication" />
                </p>
                <p className={css.subHeading}>
                  <FormattedMessage
                    id="CheckoutPage.PurchaseType.authenticationSubheading"
                    values={{ advanceShippingCostFormatted: advanceShippingCostFormatted }}
                  />
                  <p className={css.priceLabel}>{advanceShippingCostFormatted}</p>
                </p>
                {hasCountrySelected ? (
                  <p className={css.subHeading}>
                    <FormattedMessage
                      id="CheckoutPage.PurchaseType.shippingOnly"
                      values={{ price: shippingCostFormatted }}
                    />
                    <p className={css.priceLabel}>{shippingCostFormatted}</p>
                  </p>
                ) : null}
                <p className={css.learnMore} onClick={() => handleShowAuthenticatedShipping(true)}>
                  <FormattedMessage id="CheckoutPage.PurchaseType.learnMore" />
                </p>
              </div>
            }
            value="authentication"
          />
        </div>
      )}
    </div>
  );
};

export default PurchaseType;
